import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Logo from "../components/Logo";
import Field from "../components/Field";
import PrimaryButton from "../components/PrimaryButton";
import { toast } from "react-toastify";
import { resetPassword as resetPasswordAPI } from "../api/authService";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);
  const navigate = useNavigate();

  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      toast.error("Invalid reset link. Please request a new password reset.", {
        position: "top-center",
        autoClose: 3000
      });
      setTokenValid(false);
      setTimeout(() => {
        navigate("/forgot-password");
      }, 3000);
    }
  }, [token, navigate]);

  const submit = async (e) => {
    e?.preventDefault();

    if (!password || !confirmPassword) {
      toast.warn("Please enter and confirm your new password", {
        position: "top-center",
        autoClose: 2000
      });
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters long", {
        position: "top-center",
        autoClose: 2000
      });
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match", {
        position: "top-center",
        autoClose: 2000
      });
      return;
    }

    setLoading(true);
    try {
      const data = await resetPasswordAPI(token, password);

      toast.dismiss();
      toast.success(data.message || "Password reset successful! Please login with your new password.", {
        position: "top-center",
        autoClose: 3000
      });

      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (err) {
      toast.dismiss();
      const errorMessage = err.response?.data?.message || "Failed to reset password. The link may have expired.";
      toast.error(errorMessage, {
        position: "top-center",
        autoClose: 4000
      });

      if (err.response?.status === 400 || err.response?.status === 404) {
        setTimeout(() => {
          navigate("/forgot-password");
        }, 4000);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!tokenValid) {
    return (
      <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-slate-50 to-emerald-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 transition-colors duration-300" />
        <div className="relative text-center p-8">
          <div className="text-slate-600 dark:text-slate-400">Invalid reset link. Redirecting...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {}
      <div className="absolute inset-0 bg-gradient-to-br from-white via-slate-50 to-emerald-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 transition-colors duration-300" />
      <div className="pointer-events-none absolute inset-0 opacity-[.04] dark:opacity-[.08] [background-image:url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2240%22 height=%2240%22 viewBox=%220 0 40 40%22><path fill=%22%23a1a1aa%22 fill-opacity=%220.5%22 d=%22M0 39h40v1H0zM39 0v40h1V0z%22/></svg>')]" />

      {}
      <div className="relative w-full max-w-md px-4 sm:px-6">
        {}
        <div className="flex items-center justify-center mb-8">
          <Logo />
        </div>

        {}
        <form
          onSubmit={submit}
          className="w-full bg-white/80 dark:bg-slate-800/90 backdrop-blur-md rounded-3xl
                     ring-1 ring-black/5 dark:ring-white/10 shadow-[0_20px_60px_-20px_rgba(2,6,23,0.25)] dark:shadow-[0_20px_60px_-20px_rgba(0,0,0,0.5)]
                     p-6 sm:p-8 md:p-10 space-y-6 sm:space-y-8 transition-colors"
          aria-label="Reset password form"
        >
          <header className="space-y-2 text-center">
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white">
              Reset Password
            </h1>
            <p className="text-slate-600 dark:text-slate-400 text-sm sm:text-base">
              Enter your new password below
            </p>
          </header>

          <div className="space-y-4">
            <div>
              <Field
                placeholder="New Password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <div className="text-xs text-slate-500 dark:text-slate-500 mt-1 ml-1">
                Minimum 6 characters
              </div>
            </div>

            <Field
              placeholder="Confirm New Password"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>

          <PrimaryButton
            type="submit"
            disabled={loading}
            aria-label="Reset password"
          >
            {loading ? "Resetting..." : "Reset Password"}
          </PrimaryButton>

          <div className="text-sm text-center">
            <Link to="/login" className="text-slate-600 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 font-medium transition-colors">
              Back to Login
            </Link>
          </div>
        </form>

        {}
        <div className="mt-6 text-center">
          <p className="text-xs text-slate-500 dark:text-slate-500">
            Remember your password?{" "}
            <Link to="/login" className="text-slate-700 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 font-medium transition-colors">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
